import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaChartLine,
  FaDownload,
  FaUsers,
  FaShoppingCart,
  FaRupeeSign,
  FaArrowUp,
  FaArrowDown,
  FaSync
} from 'react-icons/fa';
import './Analytics.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    summary: {
      totalRevenue: 0,
      totalOrders: 0,
      averageOrderValue: 0,
      conversionRate: 0,
      growthRate: 0
    },
    salesTrend: [],
    ordersByStatus: [],
    topProducts: [],
    topWorkshops: [],
    customerInsights: {},
    geographicalData: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  // const [viewType, setViewType] = useState('chart');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });

      const [
        summaryRes,
        trendsRes,
        statusRes,
        productsRes,
        workshopsRes,
        customersRes
      ] = await Promise.allSettled([
        axios.get(`${API_URL}/api/analytics/summary?${params}`),
        axios.get(`${API_URL}/api/analytics/sales-trends?${params}`),
        axios.get(`${API_URL}/api/analytics/orders-by-status?${params}`),
        axios.get(`${API_URL}/api/analytics/top-products?${params}`),
        axios.get(`${API_URL}/api/analytics/top-workshops?${params}`),
        axios.get(`${API_URL}/api/analytics/customer-insights?${params}`)
      ]);

      setAnalyticsData({
        summary: summaryRes.status === 'fulfilled' ? summaryRes.value.data : {},
        salesTrend: trendsRes.status === 'fulfilled' ? trendsRes.value.data : [],
        ordersByStatus: statusRes.status === 'fulfilled' ? statusRes.value.data : [],
        topProducts: productsRes.status === 'fulfilled' ? productsRes.value.data : [],
        topWorkshops: workshopsRes.status === 'fulfilled' ? workshopsRes.value.data : [],
        customerInsights: customersRes.status === 'fulfilled' ? customersRes.value.data : {}
      });

    } catch (error) {
      console.error("Error fetching analytics:", error);
      setError("Failed to load analytics data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format = 'csv') => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/export`, {
        params: {
          format,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-report-${dateRange.startDate}-to-${dateRange.endDate}.${format}`;
      a.click();
    } catch (error) {
      console.error("Error exporting report:", error);
      alert("Failed to export report");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const getStatusColor = (status) => {
    const colors = {
      delivered: '#28a745',
      shipped: '#007bff',
      confirmed: '#ffc107',
      processing: '#17a2b8',
      cancelled: '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  const MetricCard = ({ title, value, icon: Icon, trend, color = '#546140' }) => (
    <div className="metric-card">
      <div className="metric-header">
        <div className="metric-icon" style={{ backgroundColor: color }}>
          <Icon />
        </div>
        <div className="metric-trend">
          {trend > 0 ? (
            <FaArrowUp className="trend-up" />
          ) : trend < 0 ? (
            <FaArrowDown className="trend-down" />
          ) : null}
          {trend !== undefined && <span>{formatPercentage(Math.abs(trend))}</span>}
        </div>
      </div>
      <div className="metric-content">
        <h3>{value}</h3>
        <p>{title}</p>
      </div>
    </div>
  );

  const SalesChart = ({ data }) => (
    <div className="chart-container">
      <div className="chart-header">
        <h3>Sales Trend</h3>
        <div className="chart-controls">
          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
          >
            <option value="revenue">Revenue</option>
            <option value="orders">Orders</option>
            <option value="customers">New Customers</option>
          </select>
        </div>
      </div>
      <div className="simple-chart">
        {data.length === 0 ? (
          <div className="no-data">
            <FaChartLine />
            <p>No data available for selected period</p>
          </div>
        ) : (
          <div className="chart-bars">
            {data.map((item, index) => (
              <div key={index} className="chart-bar">
                <div 
                  className="bar" 
                  style={{ 
                    height: `${(item[selectedMetric] / Math.max(...data.map(d => d[selectedMetric]))) * 100}%` 
                  }}
                  title={`${item.date}: ${item[selectedMetric]}`}
                ></div>
                <span className="bar-label">{new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const StatusChart = ({ data }) => (
    <div className="chart-container">
      <h3>Orders by Status</h3>
      <div className="status-chart">
        {data.map((item, index) => (
          <div key={index} className="status-item">
            <div className="status-info">
              <div 
                className="status-color" 
                style={{ backgroundColor: getStatusColor(item._id) }}
              ></div>
              <span className="status-name">{item._id}</span>
            </div>
            <div className="status-value">
              <span className="count">{item.count}</span>
              <span className="percentage">
                {((item.count / data.reduce((sum, d) => sum + d.count, 0)) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const TopItemsList = ({ title, items, type }) => (
    <div className="top-items">
      <h3>{title}</h3>
      <div className="items-list">
        {items.length === 0 ? (
          <p>No data available</p>
        ) : (
          items.map((item, index) => (
            <div key={index} className="item-row">
              <div className="item-rank">#{index + 1}</div>
              <div className="item-info">
                <span className="item-name">{item.name}</span>
                <span className="item-stats">
                  {type === 'products' ? (
                    <>Sold: {item.totalSold} | Revenue: {formatCurrency(item.totalRevenue)}</>
                  ) : (
                    <>Bookings: {item.totalBookings} | Revenue: {formatCurrency(item.totalRevenue)}</>
                  )}
                </span>
              </div>
              <div className="item-progress">
                <div 
                  className="progress-bar"
                  style={{ width: `${(item.totalRevenue / items[0].totalRevenue) * 100}%` }}
                ></div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="analytics">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics">
        <div className="error-state">
          <h3>Error Loading Analytics</h3>
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="retry-btn">
            <FaSync /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics">
      <div className="analytics-header">
        <div className="header-content">
          <h1>Analytics & Reports</h1>
          <p>Insights into your business performance</p>
        </div>
        <div className="header-actions">
          <div className="date-range">
            <label>From:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
            />
            <label>To:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
            />
          </div>
          <button className="btn btn-outline" onClick={() => exportReport('csv')}>
            <FaDownload /> Export CSV
          </button>
          <button className="btn btn-primary" onClick={() => exportReport('pdf')}>
            <FaDownload /> Export PDF
          </button>
          <button className="btn btn-secondary" onClick={fetchAnalytics}>
            <FaSync /> Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(analyticsData.summary.totalRevenue || 0)}
          icon={FaRupeeSign}
          trend={analyticsData.summary.revenueGrowth}
          color="#28a745"
        />
        <MetricCard
          title="Total Orders"
          value={analyticsData.summary.totalOrders || 0}
          icon={FaShoppingCart}
          trend={analyticsData.summary.orderGrowth}
          color="#007bff"
        />
        <MetricCard
          title="Average Order Value"
          value={formatCurrency(analyticsData.summary.averageOrderValue || 0)}
          icon={FaChartLine}
          trend={analyticsData.summary.aovGrowth}
          color="#ffc107"
        />
        <MetricCard
          title="New Customers"
          value={analyticsData.summary.newCustomers || 0}
          icon={FaUsers}
          trend={analyticsData.summary.customerGrowth}
          color="#17a2b8"
        />
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        <div className="chart-section">
          <SalesChart data={analyticsData.salesTrend} />
        </div>
        <div className="chart-section">
          <StatusChart data={analyticsData.ordersByStatus} />
        </div>
      </div>

      {/* Top Performers */}
      <div className="top-performers">
        <div className="performer-section">
          <TopItemsList
            title="Top Products"
            items={analyticsData.topProducts}
            type="products"
          />
        </div>
        <div className="performer-section">
          <TopItemsList
            title="Top Workshops"
            items={analyticsData.topWorkshops}
            type="workshops"
          />
        </div>
      </div>

      {/* Customer Insights */}
      <div className="insights-section">
        <div className="insights-header">
          <h2>Customer Insights</h2>
        </div>
        <div className="insights-grid">
          <div className="insight-card">
            <h4>Customer Retention</h4>
            <div className="insight-value">
              {formatPercentage(analyticsData.customerInsights.retentionRate || 0)}
            </div>
            <p>of customers made repeat purchases</p>
          </div>
          <div className="insight-card">
            <h4>Average Customer Lifetime</h4>
            <div className="insight-value">
              {analyticsData.customerInsights.averageLifetime || 0} days
            </div>
            <p>average customer relationship duration</p>
          </div>
          <div className="insight-card">
            <h4>Most Popular Category</h4>
            <div className="insight-value">
              {analyticsData.customerInsights.popularCategory || 'N/A'}
            </div>
            <p>highest selling product category</p>
          </div>
          <div className="insight-card">
            <h4>Peak Hours</h4>
            <div className="insight-value">
              {analyticsData.customerInsights.peakHours || 'N/A'}
            </div>
            <p>when customers shop most</p>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="recommendations">
        <h3>Recommendations</h3>
        <div className="recommendation-list">
          {analyticsData.summary.totalOrders === 0 && (
            <div className="recommendation">
              <FaShoppingCart className="rec-icon" />
              <div>
                <h4>No orders yet</h4>
                <p>Focus on marketing and customer acquisition to get your first orders.</p>
              </div>
            </div>
          )}
          
          {analyticsData.summary.averageOrderValue < 1000 && analyticsData.summary.totalOrders > 0 && (
            <div className="recommendation">
              <FaArrowUp className="rec-icon" />
              <div>
                <h4>Increase Average Order Value</h4>
                <p>Your AOV is {formatCurrency(analyticsData.summary.averageOrderValue)}. Consider bundling products or offering workshops to increase this.</p>
              </div>
            </div>
          )}

          {analyticsData.ordersByStatus.find(s => s._id === 'cancelled')?.count > 0 && (
            <div className="recommendation">
              <FaArrowDown className="rec-icon warning" />
              <div>
                <h4>Reduce Cancellations</h4>
                <p>You have {analyticsData.ordersByStatus.find(s => s._id === 'cancelled')?.count} cancelled orders. Review your fulfillment process.</p>
              </div>
            </div>
          )}

          {analyticsData.topProducts.length > 0 && (
            <div className="recommendation">
              <FaChartLine className="rec-icon success" />
              <div>
                <h4>Promote Top Products</h4>
                <p>Your top product "{analyticsData.topProducts[0]?.name}" is performing well. Consider featuring it prominently or creating similar products.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics; 